import type { ConsentData, GoogleConsentModeData } from '@/core/types';

declare global {
  interface Window {
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
  }
}

export class GoogleConsentIntegration {
  private static instance: GoogleConsentIntegration | null = null;
  private isInitialized = false;
  private projectId: string;
  private debugging: boolean;

  private constructor(projectId: string, debugging = false) {
    this.projectId = projectId;
    this.debugging = debugging;
  }

  public static getInstance(
    projectId: string,
    debugging = false
  ): GoogleConsentIntegration {
    if (!GoogleConsentIntegration.instance) {
      GoogleConsentIntegration.instance = new GoogleConsentIntegration(
        projectId,
        debugging
      );
    }
    return GoogleConsentIntegration.instance;
  }

  public initialize(): void {
    if (this.isInitialized) return;

    this.ensureGtagExists();
    this.setDefaultConsent();
    this.isInitialized = true;

    this.log('Google Consent Mode v2 initialized');
  }

  public updateConsent(consent: ConsentData): void {
    if (!this.isInitialized) {
      this.initialize();
    }

    const consentMode = this.mapConsentToGoogleMode(consent);

    if (window.gtag) {
      window.gtag('consent', 'update', consentMode);
      this.log('Updated Google Consent Mode:', consentMode);

      this.sendConsentEvent(consent);
    }
  }

  public setDefaultConsent(region?: string): void {
    this.ensureGtagExists();

    const defaultConsent = this.getRegionalDefaultConsent();

    if (window.gtag) {
      window.gtag('consent', 'default', {
        ...defaultConsent,
        wait_for_update: 500,
        ads_data_redaction: true,
        url_passthrough: false,
        region: region ? [region] : undefined,
      });

      this.log('✅ Set GDPR-safe default Google Consent Mode:', defaultConsent);
    }
  }

  public grantConsent(categories: string[]): void {
    if (!window.gtag) return;

    const updateObject: Partial<GoogleConsentModeData> = {};

    categories.forEach((category) => {
      switch (category) {
        case 'analytics':
          updateObject.analytics_storage = 'granted';
          break;
        case 'advertising':
          updateObject.ad_storage = 'granted';
          updateObject.ad_user_data = 'granted';
          updateObject.ad_personalization = 'granted';
          break;
        case 'functional':
          updateObject.functionality_storage = 'granted';
          break;
        case 'personalization':
          updateObject.personalization_storage = 'granted';
          break;
      }
    });

    if (Object.keys(updateObject).length > 0) {
      window.gtag('consent', 'update', updateObject);
      this.log('Granted consent for categories:', categories, updateObject);
    }
  }

  public denyConsent(categories: string[]): void {
    if (!window.gtag) return;

    const updateObject: Partial<GoogleConsentModeData> = {};

    categories.forEach((category) => {
      switch (category) {
        case 'analytics':
          updateObject.analytics_storage = 'denied';
          break;
        case 'advertising':
          updateObject.ad_storage = 'denied';
          updateObject.ad_user_data = 'denied';
          updateObject.ad_personalization = 'denied';
          break;
        case 'functional':
          updateObject.functionality_storage = 'denied';
          break;
        case 'personalization':
          updateObject.personalization_storage = 'denied';
          break;
      }
    });

    if (Object.keys(updateObject).length > 0) {
      window.gtag('consent', 'update', updateObject);
      this.log('Denied consent for categories:', categories, updateObject);
    }
  }

  public getCurrentConsentState(): GoogleConsentModeData | null {
    if (!window.gtag) return null;

    try {
      let currentState: GoogleConsentModeData | null = null;

      window.gtag('get', 'consent', (consent: GoogleConsentModeData) => {
        currentState = consent;
      });

      return currentState;
    } catch (error) {
      this.log('Failed to get current consent state:', error);
      return null;
    }
  }

  public injectGoogleAnalytics(measurementId: string): void {
    if (!measurementId || typeof window === 'undefined') return;

    if (!window.gtag) {
      this.ensureGtagExists();
    }

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;

    script.onload = () => {
      if (window.gtag) {
        window.gtag('config', measurementId);
        this.log(`Google Analytics configured with ID: ${measurementId}`);
      }
    };

    document.head.appendChild(script);
  }

  public injectGoogleTagManager(gtmId: string): void {
    if (!gtmId || typeof window === 'undefined') return;

    window.dataLayer = window.dataLayer || [];

    const script = document.createElement('script');
    script.innerHTML = `
      (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
      new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
      j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
      'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
      })(window,document,'script','dataLayer','${gtmId}');
    `;

    document.head.appendChild(script);

    const noscript = document.createElement('noscript');
    noscript.innerHTML = `
      <iframe src="https://www.googletagmanager.com/ns.html?id=${gtmId}"
      height="0" width="0" style="display:none;visibility:hidden"></iframe>
    `;

    if (document.body) {
      document.body.appendChild(noscript);
    } else {
      document.addEventListener('DOMContentLoaded', () => {
        document.body.appendChild(noscript);
      });
    }

    this.log(`Google Tag Manager configured with ID: ${gtmId}`);
  }

  private ensureGtagExists(): void {
    if (typeof window === 'undefined') return;

    if (!window.dataLayer) {
      window.dataLayer = [];
    }

    if (!window.gtag) {
      window.gtag = function (...args: any[]) {
        window.dataLayer?.push(args);
      };
    }
  }

  private mapConsentToGoogleMode(consent: ConsentData): GoogleConsentModeData {
    return {
      ad_storage: consent.advertising ? 'granted' : 'denied',
      analytics_storage: consent.analytics ? 'granted' : 'denied',
      ad_user_data: consent.advertising ? 'granted' : 'denied',
      ad_personalization: consent.advertising ? 'granted' : 'denied',
      functionality_storage: consent.functional ? 'granted' : 'denied',
      personalization_storage: consent.personalization ? 'granted' : 'denied',
      security_storage: 'granted', // Always granted for essential functionality
    };
  }

  private getRegionalDefaultConsent(): GoogleConsentModeData {
    // GDPR-SAFE: Always default to 'denied' for non-essential categories
    // This ensures compliance globally regardless of region detection
    return {
      ad_storage: 'denied',
      analytics_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
      functionality_storage: 'denied',
      personalization_storage: 'denied',
      security_storage: 'granted', // Only essential storage is granted by default
    };
  }

  private sendConsentEvent(consent: ConsentData): void {
    if (!window.gtag) return;

    try {
      window.gtag('event', 'consent_update', {
        event_category: 'consent',
        event_label: 'user_consent_update',
        custom_parameters: {
          consent_analytics: consent.analytics,
          consent_advertising: consent.advertising,
          consent_functional: consent.functional,
          consent_personalization: consent.personalization,
          consent_timestamp: consent.timestamp,
          consent_version: consent.version,
          project_id: this.projectId,
        },
      });

      this.log('Sent consent update event to Google Analytics');
    } catch (error) {
      this.log('Failed to send consent event:', error);
    }
  }

  public trackConsentInteraction(
    action:
      | 'banner_shown'
      | 'banner_accept'
      | 'banner_reject'
      | 'banner_customize'
  ): void {
    if (!window.gtag) return;

    try {
      window.gtag('event', 'consent_interaction', {
        event_category: 'consent',
        event_action: action,
        event_label: 'consent_banner',
        custom_parameters: {
          project_id: this.projectId,
          timestamp: Date.now(),
        },
      });

      this.log(`Tracked consent interaction: ${action}`);
    } catch (error) {
      this.log('Failed to track consent interaction:', error);
    }
  }

  public validateConsentMode(): boolean {
    if (!window.gtag) {
      this.log('gtag not available for consent mode validation');
      return false;
    }

    try {
      window.gtag('get', 'consent', (consent: GoogleConsentModeData) => {
        this.log('Current consent state validation:', consent);
      });
      return true;
    } catch (error) {
      this.log('Consent mode validation failed:', error);
      return false;
    }
  }

  public async measureConsentPerformance(): Promise<{
    initTime: number;
    updateTime: number;
    status: 'healthy' | 'degraded' | 'error';
  }> {
    const startTime = performance.now();

    try {
      this.initialize();
      const initTime = performance.now() - startTime;

      const updateStartTime = performance.now();
      this.updateConsent({
        essential: true,
        analytics: true,
        advertising: false,
        functional: true,
        personalization: false,
        timestamp: Date.now(),
        version: '1.0',
      });
      const updateTime = performance.now() - updateStartTime;

      const status =
        initTime < 50 && updateTime < 20
          ? 'healthy'
          : initTime < 100 && updateTime < 50
            ? 'degraded'
            : 'error';

      return { initTime, updateTime, status };
    } catch (error) {
      this.log('Performance measurement failed:', error);
      return {
        initTime: performance.now() - startTime,
        updateTime: 0,
        status: 'error',
      };
    }
  }

  private log(message: string, ...args: any[]): void {
    if (this.debugging) {
      console.log(`[GoogleConsent] ${message}`, ...args);
    }
  }

  public destroy(): void {
    GoogleConsentIntegration.instance = null;
    this.isInitialized = false;
  }
}
